import {
  makeStyles,
  tokens,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  Checkbox,
  Text,
} from '@fluentui/react-components';
import { ColumnEdit20Regular, ReOrderDotsVertical20Regular } from '@fluentui/react-icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMailColumnPrefs, MAIL_COLUMN_META, type MailColumnId } from './mailColumns';

const useStyles = makeStyles({
  surface: { padding: tokens.spacingVerticalS, minWidth: '220px' },
  title: { display: 'block', marginBottom: tokens.spacingVerticalXS },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} 0`,
  },
  handle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'grab',
    color: tokens.colorNeutralForeground3,
    touchAction: 'none',
  },
  label: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  footer: { marginTop: tokens.spacingVerticalXS, display: 'flex', justifyContent: 'flex-end' },
  // Mention « volet trop étroit » : posée en frère de la case et non dans son
  // libellé, sinon l'ellipse du libellé la tronquerait la première.
  narrowTag: {
    marginLeft: 'auto',
    paddingLeft: tokens.spacingHorizontalXS,
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    whiteSpace: 'nowrap',
  },
});

function SortableColumnRow({ id, narrow }: { id: MailColumnId; narrow: boolean }) {
  const styles = useStyles();
  const hidden = useMailColumnPrefs((s) => s.hidden.includes(id));
  const toggle = useMailColumnPrefs((s) => s.toggle);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      className={styles.row}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <span className={styles.handle} {...attributes} {...listeners} aria-label="Réordonner">
        <ReOrderDotsVertical20Regular />
      </span>
      <Checkbox
        checked={!hidden}
        onChange={() => toggle(id)}
        label={<span className={styles.label}>{MAIL_COLUMN_META[id].label}</span>}
      />
      {/* La case reste COCHÉE — le choix de l'utilisateur est intact, c'est la
          place qui manque —, mais sans cette mention le panneau affirmerait
          quelque chose de faux : décocher puis recocher la colonne ne changerait
          rien à l'écran, et rien n'indiquerait pourquoi. Élargir le volet la fait
          revenir d'elle-même. */}
      {narrow && !hidden && (
        <Text className={styles.narrowTag} title="Élargissez le volet pour la voir">
          volet trop étroit
        </Text>
      )}
    </div>
  );
}

/**
 * Gear button (in the actions-column header) opening a popover to show/hide the
 * mail-list columns and reorder them by drag. Column order/visibility is shared
 * with the on-grid header drag via the same persisted store.
 *
 * `narrowHidden` : colonnes cochées mais que le volet est trop étroit pour porter
 * (cf. columnLayout.fitMiddleColumns). Ce n'est PAS une préférence — rien n'est
 * persisté — mais le panneau doit le dire, sous peine de montrer cochée une
 * colonne absente de la grille.
 */
export function ColumnSettingsPopover({
  narrowHidden = [],
}: {
  narrowHidden?: readonly MailColumnId[];
}) {
  const styles = useStyles();
  const order = useMailColumnPrefs((s) => s.order);
  const reorder = useMailColumnPrefs((s) => s.reorder);
  const reset = useMailColumnPrefs((s) => s.reset);
  // A small activation distance so a click on the handle doesn't count as a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = (e: DragEndEvent) => {
    const activeId = e.active.id as MailColumnId;
    const overId = e.over?.id as MailColumnId | undefined;
    if (overId && activeId !== overId) reorder(activeId, overId);
  };

  return (
    <Popover trapFocus withArrow positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<ColumnEdit20Regular />}
          aria-label="Paramétrer les colonnes"
          title="Colonnes"
        />
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <Text className={styles.title} weight="semibold" size={200}>
          Colonnes affichées
        </Text>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((id) => (
              <SortableColumnRow key={id} id={id} narrow={narrowHidden.includes(id)} />
            ))}
          </SortableContext>
        </DndContext>
        <div className={styles.footer}>
          <Button appearance="subtle" size="small" onClick={reset}>
            Réinitialiser
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}
